import { useEffect } from 'react';

export const GOOGLE_ANALYTICS_ID = 'G-RCFLCL1V3N';
export const ANALYTICS_CONSENT_CHANGED_EVENT = 'mellomrom:analytics-consent-changed';
export const OPEN_ANALYTICS_SETTINGS_EVENT = 'mellomrom:open-analytics-settings';

const CONSENT_STORAGE_KEY = 'mellomrom.analytics-consent.v1';
const SCRIPT_ID = 'mellomrom-google-analytics';

type AnalyticsConsent = 'granted' | 'denied';
type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: Gtag;
    mellomromGoogleAnalyticsConfigured?: boolean;
  }
}

let sessionConsent: AnalyticsConsent | null = null;
let appliedConsent: AnalyticsConsent | null = null;
let lastPageKey: string | null = null;

export function readAnalyticsConsent(): AnalyticsConsent | null {
  if (sessionConsent) return sessionConsent;
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null;
  }
}

export function saveAnalyticsConsent(consent: AnalyticsConsent) {
  sessionConsent = consent;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, consent);
  } catch {
    // The choice still applies for the current page when storage is unavailable.
  }

  ensureGoogleAnalytics();
  applyAnalyticsConsent(consent);
  if (consent === 'granted') lastPageKey = null;
  else clearGoogleAnalyticsCookies();
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT));
}

export function openAnalyticsSettings() {
  window.dispatchEvent(new Event(OPEN_ANALYTICS_SETTINGS_EVENT));
}

export function useGoogleAnalyticsPageView(pagePath: string | null, pageTitle: string) {
  useEffect(() => {
    function sendPageView() {
      if (!pagePath) return;
      ensureGoogleAnalytics();
      applyAnalyticsConsent(readAnalyticsConsent() === 'granted' ? 'granted' : 'denied');
      const pageKey = `${pagePath}:${pageTitle}`;
      if (lastPageKey === pageKey) return;
      lastPageKey = pageKey;
      window.gtag?.('event', 'page_view', {
        page_location: new URL(pagePath, window.location.origin).href,
        page_path: pagePath,
        page_title: pageTitle
      });
    }

    sendPageView();
    window.addEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sendPageView);
    return () => window.removeEventListener(ANALYTICS_CONSENT_CHANGED_EVENT, sendPageView);
  }, [pagePath, pageTitle]);
}

function ensureGoogleAnalytics() {
  (window as unknown as Record<string, unknown>)[`ga-disable-${GOOGLE_ANALYTICS_ID}`] = false;
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(..._arguments: unknown[]) {
      window.dataLayer?.push(arguments);
    };
  }

  if (!window.mellomromGoogleAnalyticsConfigured) {
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500
    });
    window.gtag('set', 'ads_data_redaction', true);
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ANALYTICS_ID, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    });
    window.mellomromGoogleAnalyticsConfigured = true;
  }

  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
    document.head.append(script);
  }
}

function applyAnalyticsConsent(consent: AnalyticsConsent) {
  if (appliedConsent === consent) return;
  window.gtag?.('consent', 'update', {
    analytics_storage: consent,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  appliedConsent = consent;
}

function clearGoogleAnalyticsCookies() {
  const names = document.cookie.split(';')
    .map(cookie => cookie.slice(0, cookie.indexOf('=')).trim())
    .filter(name => name === '_ga' || name.startsWith('_ga_'));
  const hostParts = window.location.hostname.split('.');
  const parentDomain = hostParts.length > 1 ? `.${hostParts.slice(-2).join('.')}` : null;

  for (const name of names) {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=${window.location.hostname}`;
    if (parentDomain) document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax; Domain=${parentDomain}`;
  }
}
